/**
 * Client-facing billing types for Circuit Forge.
 *
 * Naming mirrors the surface in `/Users/heinrichdelange/.claude/plans/polymorphic-moseying-diffie.md`
 * (the approved billing UX plan). All credit values are integer "credits"
 * — the abstract user-facing unit (~$0.01 of LLM-equivalent value at the
 * launch markup). Tokens, USD-per-token, and Anthropic SKUs do not appear
 * in the client surface.
 *
 * Backend wire format isn't fixed yet; these types are the contract the
 * mock layer satisfies and the real API will be expected to match.
 */

// ── Plans ───────────────────────────────────────────────────────────────────

/** The four plan kinds a billable account can be on. */
export type PlanId = "trial" | "pro" | "team" | "none";

/** Snapshot of the active subscription/plan for a billing scope. */
export interface Plan {
  id: PlanId;
  /** "Trial", "Pro", "Team" — what the UI labels things. Owner-vs-member
   * is encoded in `read_only` rather than baked into the label. */
  display_name: string;
  /** Cents per cycle for the listed price (Pro: 2500, Team: 3000/seat). 0 for trial / none. */
  price_cents: number;
  /** "month" — only monthly billing at launch. */
  interval: "month";
  /** Number of seats currently provisioned (Team only; 1 elsewhere). */
  seats: number;
  /** ISO timestamp when this cycle ends (renewal or trial expiry). null on `none`. */
  current_period_end: string | null;
  /** True for Team plans where the caller is a member but not owner/admin. */
  read_only: boolean;
  /** True if Stripe says payment failed and we're past grace; UI should warn loudly. */
  past_due: boolean;
  /** Trial-specific: ISO when the 500-credit allotment expires. */
  trial_ends_at: string | null;
}

// ── Wallet ──────────────────────────────────────────────────────────────────

/**
 * Live wallet snapshot for the active scope. Sub-balances stack — the
 * `balance` field is the sum of the four buckets and is what we display
 * in the sidebar pill.
 *
 * Burn order (most-perishable first): trial → included → rollover → topup.
 * The backend enforces the same order; clients display the breakdown but
 * never compute spend themselves.
 */
export interface Wallet {
  scope: BillingScope;
  /** Total spendable credits = trial + included + rollover + topup. */
  balance: number;
  trial_credits: number;
  included_credits: number;
  rollover_credits: number;
  topup_credits: number;
  /** ISO timestamp; null if not on a paid plan. */
  next_reset_at: string | null;
  /** Auto-recharge configuration; `enabled: false` means user has not opted in. */
  auto_recharge: AutoRechargeConfig;
}

export interface AutoRechargeConfig {
  enabled: boolean;
  /** Trigger threshold — recharge fires when balance drops below this many credits. */
  threshold_credits: number;
  /** Pack ID to charge on each fire (ties to TopUpPack.id). */
  pack_id: TopUpPackId | null;
}

// ── Top-up packs ────────────────────────────────────────────────────────────

export type TopUpPackId = "p1k" | "p5_5k" | "p24k";

/**
 * Sticker price for one-shot credit purchases. Bonus credits are baked
 * into `credits` already (e.g. p5_5k advertises 5,500 = 5,000 + 10%).
 */
export interface TopUpPack {
  id: TopUpPackId;
  /** What we charge in cents. */
  price_cents: number;
  /** Total credits the user receives. */
  credits: number;
  /** % bonus over the base $-to-credit rate; 0 for the entry pack. */
  bonus_pct: number;
  /** Marketing label for the card. */
  label: string;
}

/** Static catalog — the source of truth that pricing pages and modals read. */
export const TOP_UP_PACKS: readonly TopUpPack[] = [
  {
    id: "p1k",
    price_cents: 1000,
    credits: 1_000,
    bonus_pct: 0,
    label: "Starter",
  },
  {
    id: "p5_5k",
    price_cents: 5000,
    credits: 5_500,
    bonus_pct: 10,
    label: "Standard",
  },
  {
    id: "p24k",
    price_cents: 20000,
    credits: 24_000,
    bonus_pct: 20,
    label: "Bulk",
  },
] as const;

// ── Invoices ────────────────────────────────────────────────────────────────

export type InvoiceStatus = "paid" | "open" | "uncollectible" | "void";

export interface Invoice {
  id: string;
  /** ISO date the invoice was issued. */
  issued_at: string;
  /** Human description ("Pro · Apr 2026", "Top-up · 5,500 credits"). */
  description: string;
  amount_cents: number;
  status: InvoiceStatus;
  /** Stripe-hosted PDF URL. null while async-generating. */
  pdf_url: string | null;
}

// ── Payment methods ─────────────────────────────────────────────────────────

export interface PaymentMethod {
  id: string;
  /** "visa" / "mc" / "amex" / "discover" / "unknown". Drives the brand glyph. */
  brand: string;
  /** Last four digits of the card. */
  last4: string;
  /** Expiry — "MM/YY" formatted by the API. */
  exp: string;
  is_default: boolean;
}

// ── Usage breakdown ─────────────────────────────────────────────────────────

/**
 * One bucket of credit spend over a day. Categories are coarse enough
 * to be readable in a stacked area chart without exploding the legend.
 */
export type UsageCategory =
  | "sonnet_chat"
  | "opus_chat"
  | "spice"
  | "subagents"
  | "other";

export interface UsageBreakdownPoint {
  /** ISO date (YYYY-MM-DD), one row per day. */
  date: string;
  totals: Partial<Record<UsageCategory, number>>;
}

export interface UsageBreakdown {
  scope: BillingScope;
  /** "7d", "30d", "cycle" — echoed back so we can sanity-check the response. */
  range: UsageRange;
  points: UsageBreakdownPoint[];
}

export type UsageRange = "7d" | "30d" | "cycle";

// ── Per-seat usage (Team plan org admins) ───────────────────────────────────

export interface SeatUsage {
  user_id: string;
  display_name: string;
  email: string;
  /** Credits this seat has spent in the current billing cycle. */
  credits_used: number;
}

// ── Scope ───────────────────────────────────────────────────────────────────

/**
 * Which billable account a request targets — personal or a specific org.
 * Stored as the React Query cache key.
 */
export type BillingScope = "personal" | { org_id: string };

export function scopeKey(
  scope: BillingScope | string | null | undefined,
): string {
  if (!scope || scope === "personal") return "personal";
  if (typeof scope === "string") return scope;
  return scope.org_id;
}

// ── Credit estimate (chat composer pre-flight) ──────────────────────────────

export type ChatModelId = "sonnet" | "opus";

/**
 * Per-1k-token credit cost. Markup is baked in here — these are the
 * numbers the user reasons about, not the raw Anthropic rates.
 *
 * Tweak with marketing/finance before launch. Conversion was illustrated
 * in the approved plan as 1.5x markup over Anthropic; the ratios here
 * preserve Opus ≈ 5x Sonnet, output ≈ 5x input within each model.
 */
export const CREDITS_PER_1K_TOKENS: Record<
  ChatModelId,
  { input: number; output: number }
> = {
  sonnet: { input: 0.5, output: 2.5 },
  opus: { input: 2.5, output: 12 },
};

/**
 * Rough composer-side estimate of what a turn will cost. Server settles
 * the actual amount post-turn — this is purely advisory.
 *
 * Heuristic: the prompt tokens dominate input cost; output is bounded by
 * the model's typical reply length (~600 tokens for a substantive answer).
 * One token ≈ 4 chars of English; we guard against absurdly small drafts
 * by flooring the input at 200 tokens (system prompt + history weighting).
 */
export function estimateTurnCost(
  draftChars: number,
  model: ChatModelId,
  expectedOutputTokens = 600,
): number {
  const inputTokens = Math.max(200, Math.ceil(draftChars / 4));
  const rate = CREDITS_PER_1K_TOKENS[model];
  const credits =
    (inputTokens / 1000) * rate.input +
    (expectedOutputTokens / 1000) * rate.output;
  return Math.max(1, Math.round(credits));
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export function formatCredits(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n === 0) return "0";
  if (n < 1000) return String(n);
  if (n < 100_000) return n.toLocaleString();
  return `${(n / 1000).toFixed(0)}k`;
}

export function formatCents(c: number): string {
  if (c % 100 === 0) return `$${c / 100}`;
  return `$${(c / 100).toFixed(2)}`;
}
